import React, { useEffect, useState } from 'react';
import type { AppContext } from '../app';
import { getPersonalHelper } from '../ai/PersonalHelper';
import type { HelperState } from '../ai/types';

interface Props {
  ctx: AppContext;
}

/**
 * AIFab — floating action button for Phase 70 (Personal AI helper).
 *
 * Visible across every unlocked surface; tapping bridges to
 * `personal-ai-chat`. Shows a pulse while the helper is generating.
 *
 * Lives just above the SASS tab bar on mobile and bottom-right on
 * desktop. Hidden when the user is already on personal-ai-chat to
 * avoid covering the input.
 */
export function AIFab({ ctx }: Props): JSX.Element | null {
  const [state, setState] = useState<HelperState>(() => getPersonalHelper(ctx).state());
  const [activeId, setActiveId] = useState<string | null>(() => ctx.director.state().active);

  useEffect(() => {
    const off = getPersonalHelper(ctx).onState(setState);
    return off;
  }, [ctx]);

  useEffect(() => {
    return ctx.director.subscribe((s) => setActiveId(s.active));
  }, [ctx]);

  if (activeId === 'personal-ai-chat') return null;

  const stateGlyph =
    state.kind === 'generating' ? '◌' :
    state.kind === 'loading' ? '◐' :
    state.kind === 'error' ? '⚠' :
    state.kind === 'unsupported' ? '⌀' :
    state.kind === 'ready' ? '✦' :
    '✦';

  return (
    <button
      type="button"
      className={`ai-fab ai-fab-${state.kind}`}
      data-testid="ai-fab"
      aria-label="Open AI helper"
      onClick={() => ctx.director.bridge('personal-ai-chat', 'open AI helper')}
    >
      <span className="ai-fab-glyph" aria-hidden>{stateGlyph}</span>
      <span className="ai-fab-label">helper</span>
      <style>{fabStyles}</style>
    </button>
  );
}

const fabStyles = `
.ai-fab {
  position: fixed;
  right: 16px;
  bottom: 80px;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--bg-2);
  color: var(--phos);
  border: 1px solid var(--phos);
  border-radius: 999px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}
.ai-fab:hover { background: var(--bg-3, var(--bg-2)); }
.ai-fab-glyph { font-size: 16px; line-height: 1; }
.ai-fab-label { letter-spacing: 0.22em; }
.ai-fab-generating .ai-fab-glyph,
.ai-fab-loading .ai-fab-glyph { animation: ai-fab-pulse 1.2s ease-in-out infinite; }
.ai-fab-error { border-color: var(--amber); color: var(--amber); }
.ai-fab-unsupported { opacity: 0.55; }
@keyframes ai-fab-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}
@media (max-width: 700px) {
  .ai-fab { bottom: 70px; right: 12px; padding: 8px 12px; font-size: 10px; }
  .ai-fab-label { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .ai-fab-generating .ai-fab-glyph,
  .ai-fab-loading .ai-fab-glyph { animation: none; }
}
`;
